import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from 'app/hooks/usePermissions';
import Company from 'app/interfaces/Company';
import {
  NavigationConfiguration,
  ProductionDefaultDateConfiguration,
  SystemModulesUpdate,
} from 'app/interfaces/Config';
import { ApiError } from 'app/lib/authFetch';
import ConfigService from 'app/services/configService';
import { useSessionStore } from 'app/stores/globalStore';

export interface NavigationSaveError {
  reason: 'permission' | 'session' | 'server';
  detail?: string;
}

export type ConfigStatus = 'loading' | 'ready' | 'not-configured' | 'error';

export const useConfig = () => {
  const configService = useMemo(
    () => new ConfigService(process.env.NEXT_PUBLIC_API_BASE_URL!),
    []
  );

  const { hasPermission } = usePermissions();

  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const [status, setStatus] = useState<ConfigStatus>('loading');
  const [hasFetched, setHasFetched] = useState(false);

  const setConfig = useSessionStore(state => state.setConfig);
  const config = useSessionStore(state => state.config);

  const refreshConfig = async (refresh = false) => {
    try {
      const newConfig = await configService.get(refresh);

      if (newConfig) {
        setConfig(newConfig);
        setStatus('ready');
      } else {
        setStatus('not-configured');
      }
      return newConfig;
    } catch (error) {
      console.error('Error refreshing config:', error);
      setStatus('error');
      return null;
    }
  };

  useEffect(() => {
    if (config) {
      setStatus('ready');
      return;
    }
    if (!hasFetched) {
      setHasFetched(true);
      refreshConfig();
    }
  }, [config, hasFetched]);

  async function updateCompany(company: Company) {
    try {
      await configService.UpdateCompany(company);
      setSuccess(true);
      refreshSuccess();
    } catch (error) {
      console.error('Error updating company:', error);
      setSuccess(false);
      refreshSuccess();
    }
    refreshConfig(true);
  }

  async function updateModules(modules: SystemModulesUpdate): Promise<boolean> {
    const loginUser = useSessionStore.getState().loginUser;
    if (loginUser?.userLevel !== 10 || !loginUser.token) {
      setSuccess(false);
      refreshSuccess();
      return false;
    }
    try {
      setConfig(await configService.updateModules(modules, loginUser.token));
      setSuccess(true);
      refreshSuccess();
      return true;
    } catch (error) {
      console.error('Error updating system modules:', error);
      setSuccess(false);
      refreshSuccess();
      return false;
    }
  }

  async function updateNavigation(
    navigation: NavigationConfiguration | null
  ): Promise<NavigationSaveError | null> {
    const token = useSessionStore.getState().loginUser?.token;
    if (!hasPermission('config.edit')) {
      setSuccess(false);
      refreshSuccess();
      return { reason: 'permission' };
    }
    try {
      setConfig(await configService.updateNavigation(navigation, token));
      setSuccess(true);
      refreshSuccess();
      return null;
    } catch (error) {
      console.error('Error updating navigation:', error);
      setSuccess(false);
      refreshSuccess();
      if (error instanceof ApiError) {
        return error.status === 401
          ? { reason: 'session' }
          : { reason: 'server', detail: error.code };
      }
      return { reason: 'server' };
    }
  }

  async function updateProductionDefaultDate(
    value: ProductionDefaultDateConfiguration
  ): Promise<boolean> {
    const token = useSessionStore.getState().loginUser?.token;
    try {
      setConfig(await configService.updateProductionDefaultDate(value, token));
      return true;
    } catch {
      return false;
    }
  }

  function refreshSuccess() {
    setTimeout(() => {
      setSuccess(undefined);
    }, 3000);
  }

  async function getCompany() {
    const company = await configService.getCompany();
    setConfig(company);
  }

  return {
    config,
    status,
    updateCompany,
    updateModules,
    updateNavigation,
    updateProductionDefaultDate,
    getCompany,
    success,
  };
};
